import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import Pagination from '@/components/CommonComponents/Pagination';
import StatCard from '@/components/CommonComponents/StatCard';
import ViewDetailsModal from '@/components/CommonComponents/ViewDetailsModal';
import { showToast } from '@/helper/toast.helper';
import { desanitizeKey } from '@/helper/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { adminUserService } from '@/services/adminUserService/adminUserService';
import { AppDispatch, RootState } from '@/store';
import {
  addUserInList,
  deleteUser,
  deleteUserInList,
  fetchAdminUsers,
  updateUser,
  updateUserInList,
  userInfo,
} from '@/store/slices/adminUsersSlice/adminUserSlice';
import { AdminUsers, Project, Site } from '@/types/adminuser.types';
import { RowPermissions } from '@/types/surveySet.types';
import { CircleUser, Eye, EyeOff, Star } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DeleteModal from '../CommonComponents/DeleteModal';
import ServerSearchFilter, { ServerFilterField } from '../CommonComponents/ServerSearchFilter';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';
import { createAuthorizer } from '../authguards/casbin/enforcer';
import ProjectUsersModal from './ProjectUsersModal';

const roleColors: Record<string, string> = {
  SUPER_ADMIN: 'bg-[#CC167C] text-[#1A0206]',
  ADMIN: 'bg-[#4CBEE7CC] text-[#1A0206]',
  CONTENT_MODERATOR: 'bg-[#4C86E7CC] text-[#1A0206]',
  VIEWER: 'bg-[#5CC9A7CC] text-[#1A0206]',
  SURVEY_MANAGER: 'bg-[#E86A9BCC] text-[#1A0206]',
};

const loggedInRoleStr = localStorage.getItem('roleInfo');
const loggedInRoleName = loggedInRoleStr ? JSON.parse(loggedInRoleStr).name : undefined;

const SystemAdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { exportFile } = useExportFile();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<AdminUsers>();
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [filterState, setFilterState] = useState<Record<string, any>>({});
  const [filteredData, setFilteredData] = useState<AdminUsers[]>([]);
  const [isProjectUsersModalOpen, setIsProjectUsersModalOpen] = useState(false);
  const [selectedUserForProject, setSelectedUserForProject] = useState<AdminUsers | null>(null);

  const debouncedFilters = useDebounce(filterState, 500);

  const handleViewProject = useCallback((user: AdminUsers) => {
    setSelectedUserForProject(user);
    setIsProjectUsersModalOpen(true);
  }, []);

  const systemAdminColumns: Column<AdminUsers>[] = [
    // As per Harish, hiding User Name column
    // { key: 'blueNumberId', header: 'User Name' },
    {
      key: 'id',
      header: 'ID',
      render: (_, row) => `${row.id}`,
    },
    {
      key: 'countryCode',
      header: 'Country',
      render: (v) => v ?? <span className="w-1/2 block text-center text-[#000000]">N/A</span>,
    },
    {
      key: 'phoneNumber',
      header: 'Mobile Number',
      render: (_, row) => `${row.phoneNumber}`,
    },
    {
      key: 'role',
      header: 'Assigned Role',
      render: (value) => (
        <span
          className={`flex justify-center items-center w-full px-3 py-1 rounded-2xl text-xs font-medium ${
            roleColors[value?.name] || 'bg-gray-200'
          }`}
        >
          {desanitizeKey(value?.name)}
        </span>
      ),
    },
    {
      key: 'projects',
      header: 'Assigned Project (s)',
      render: (projects) => {
        if (!projects?.length) return <span>N/A</span>;
        return <span>{projects[0]?.name}</span>;
      },
    },
    {
      key: 'sites',
      header: 'Assigned Site(s)',
      render: (_, row) => {
        const sites = row.projects
          ?.flatMap((p: Project) => p.sites || [])
          ?.map((s: Site) => s?.name)
          ?.filter(Boolean);

        if (!sites?.length) return <span>N/A</span>;
        return <span>{sites[0]}</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
    },
    {
      key: 'createdAt',
      header: 'Created On',
      render: (value) => <span>{value?.split('T')[0]}</span>,
    },
    {
      key: 'viewProject',
      header: 'View Project',
      render: (_, row) => (
        <button
          onClick={() => handleViewProject(row)}
          className="px-3 py-1 bg-[#CC167C] text-white rounded hover:bg-[#AD0C62] transition-colors text-sm"
        >
          View Project
        </button>
      ),
    },
  ];

  const filters = useMemo(
    () => ({
      search: debouncedFilters?.search || undefined,
      status:
        debouncedFilters?.status && debouncedFilters?.status !== 'All Status'
          ? debouncedFilters?.status === 'Active'
          : undefined,
      currentPage: currentPage,
      pageSize: pageSize,
    }),
    [debouncedFilters, currentPage, pageSize],
  );

  const { data, loading } = useSelector((state: RootState) => state.user);
  const userCountInfo = useSelector((state: RootState) => state.user.userInfoCount);
  const metaData = useSelector((state: RootState) => state.user.meta);
  const prevFiltersRef = useRef<string>('');

  const adminStats = useMemo(
    () => [
      {
        id: 'super',
        title: 'Super Admins',
        value: userCountInfo.SUPER_ADMIN || 0,
        icon: Star,
        iconBg: 'bg-[#CC167CCC]',
      },
      {
        id: 'total',
        title: 'Total Admins',
        value: userCountInfo.ADMIN || 0,
        icon: CircleUser,
        iconBg: 'bg-[#4CBEE7CC]',
      },
      {
        id: 'viewers',
        title: 'Viewers',
        value: userCountInfo.VIEWER || 0,
        icon: Eye,
        iconBg: 'bg-[#0BA96F80]',
      },
      {
        id: 'inactive',
        title: 'Inactive Admins',
        value: userCountInfo.INACTIVE_ADMIN || 0,
        icon: EyeOff,
        iconBg: 'bg-[#E24A4A80]',
      },
    ],
    [userCountInfo],
  );

  const permissions = {
    create: usePermission('write', 'users'),
    edit: usePermission('edit', 'users'),
    delete: usePermission('delete', 'users'),
    export: usePermission('export', 'users'),
  };

  useEffect(() => {
    dispatch(userInfo());
  }, [dispatch]);
  useEffect(() => {
    // Prevent duplicate API calls if already loading or if filters haven't changed
    const currentFiltersKey = JSON.stringify(filters);
    if (prevFiltersRef.current === currentFiltersKey) {
      return;
    }
    prevFiltersRef.current = currentFiltersKey;

    dispatch(fetchAdminUsers({ ...filters }));
  }, [dispatch, filters]);

  useEffect(() => {
    let cancelled = false;

    const buildRows = async () => {
      if (!data || data.length === 0) {
        setFilteredData([]);
        return;
      }

      const rows: AdminUsers[] = await Promise.all(
        data.map(async (user) => {
          const rowPermissions: RowPermissions = { ...permissions };
          if (loggedInRoleName === 'ADMIN' && user.role && user.role?.name === 'SUPER_ADMIN') {
            rowPermissions.edit = false;
            rowPermissions.delete = false;
          }
          return {
            ...user,
            _permissions: rowPermissions,
          };
        }),
      );

      if (!cancelled) {
        setFilteredData(rows);
      }
    };

    buildRows();

    return () => {
      cancelled = true;
    };
  }, [data]);

  const getRowPermissions = async (adminUser: AdminUsers): Promise<RowPermissions> => {
    const roleInfo = JSON.parse(localStorage.getItem('roleInfo') || 'null');
    const roleName = roleInfo?.name;

    if (roleName === 'ADMIN' || roleName === 'SUPER_ADMIN') {
      return {
        edit: true,
        delete: true,
        preview: true,
        statusToggle: true,
      };
    }

    const authorizer = createAuthorizer(roleName);
    return {
      edit: await authorizer.can('edit', 'users'),
      delete: await authorizer.can('delete', 'users'),
      preview: await authorizer.can('read', 'users'),
      statusToggle: await authorizer.can('edit', 'users'),
    };
  };

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleDeleteRow = useCallback(async () => {
    if (!deleteRow) return;

    dispatch(deleteUserInList(deleteRow.id));
    try {
      await dispatch(deleteUser(deleteRow.id)).unwrap();
      showToast.success(`User deleted successfully`);
      setIsDeleteOpen(false);
      setDeleteRow(undefined);
    } catch (error) {
      dispatch(addUserInList(deleteRow));
      showToast.error(error as string);
    }
  }, [deleteRow, dispatch]);

  const openDeleteModal = useCallback((row: AdminUsers) => {
    setIsDeleteOpen(true);
    setDeleteRow(row);
  }, []);

  const handleViewUser = useCallback((user: AdminUsers) => {
    setSelectedUser({
      id: user.blueNumberId,
      phoneNumber: user.phoneNumber,
      role: desanitizeKey(user?.role?.name!),
      assignedProjects: user.projects?.map((p) => p?.name) || [],
      assignedSites: user.projects?.flatMap((p) => p.sites?.map((s) => s?.name)) || [],
      status: user.status ? 'Active' : 'Inactive',
      createdOn: user.createdAt?.split('T')[0],
    });
    setIsUserDetailsOpen(true);
  }, []);

  const handleAddAdmin = useCallback(() => {
    navigate('/dashboard/system-admin-users/create');
  }, [navigate]);

  const handleExport = useCallback(() => {
    exportFile(() => adminUserService.exportAdminUsers(), `adminUsers_${Date.now()}.csv`);
  }, [exportFile]);

  const handleStatusToggle = useCallback(
    async (id: string, newStatus: boolean) => {
      const userId = Number(id);
      const user = data.find((u) => u.id === userId);

      if (!user) return;

      const oldStatus = user.status ?? false;

      dispatch(
        updateUserInList({
          id: userId,
          payload: { status: newStatus },
        }),
      );

      try {
        await dispatch(
          updateUser({
            id: userId,
            payload: { status: newStatus },
          }),
        ).unwrap();
        dispatch(userInfo());
      } catch (error) {
        dispatch(
          updateUserInList({
            id: userId,
            payload: { status: oldStatus },
          }),
        );
        showToast.error('Failed to update status');
      }
    },
    [data, dispatch],
  );

  const handleEditUser = useCallback(
    (row: AdminUsers) => {
      navigate('/dashboard/system-admin-users/edit', {
        state: { from: 'system-admin-users', survey: row },
      });
    },
    [navigate],
  );

  const handleReviewRoles = useCallback(() => {
    navigate('/dashboard/roles-and-permissions');
  }, [navigate]);

  const filterFields: ServerFilterField[] = useMemo(
    () => [
      {
        type: 'search' as const,
        key: 'search',
        placeholder: 'Search Items...',
      },
      {
        type: 'radio-select' as const,
        key: 'status',
        label: 'Status',
        options: ['Active', 'Inactive'],
      },
    ],
    [],
  );

  return (
    <PageHeaderWrapper
      title="System Admin Users"
      description="Manage administrator accounts and permissions."
      onAdd={handleAddAdmin}
      onExport={handleExport}
      addLabel="Add Admin User"
      exportLabel="Export"
      permissions={permissions}
    >
      {/* Filters */}
      <div className="flex-shrink-0 mb-4">
        <ServerSearchFilter fields={filterFields} onChange={setFilterState} />
      </div>

      {/* Stats */}
      <div className="flex-shrink-0 mb-4">
        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
          {adminStats.map((stat) => (
            <StatCard
              key={stat.id}
              title={stat.title}
              value={stat.value?.toString()}
              icon={stat.icon}
              iconBg={stat.iconBg}
            />
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <DataTable
          listTitle="Admin List"
          listHeader={
            <div className="flex items-center justify-between w-full">
              <button
                onClick={handleReviewRoles}
                className="flex items-center gap-2 text-[#4A082D] font-Lato font-Medium px-3 py-1.5 transition"
              >
                <img src="/edit.png" alt="Edit" className="w-6 h-6" />
                Review Roles & Permissions
              </button>
            </div>
          }
          columns={systemAdminColumns}
          data={filteredData ?? []}
          loading={loading}
          onView={handleViewUser}
          onEdit={(row) => {
            if (!row._permissions?.edit) return;
            handleEditUser(row);
          }}
          onDelete={(row) => {
            if (!row._permissions?.delete) return;
            openDeleteModal(row);
          }}
          onStatusToggle={handleStatusToggle}
          permissions={permissions}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={metaData?.totalPages}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onPageChange={handlePageChange}
        />
      </div>

      <ViewDetailsModal
        isOpen={isUserDetailsOpen}
        onClose={() => setIsUserDetailsOpen(false)}
        title="User Details"
        data={selectedUser}
        fields={[
          {
            label: 'MobileNumber',
            value: selectedUser?.phoneNumber || 'N/A',
            fullWidth: true,
          },
          { label: 'Assigned Role', value: selectedUser?.role || 'N/A', fullWidth: true },
          {
            label: 'Assigned Projects',
            value:
              selectedUser?.assignedProjects.length > 0 ? selectedUser.assignedProjects : ['N/A'],
            list: true || 'N/A',
          },
          {
            label: 'Assigned Sites',
            value: selectedUser?.assignedSites?.length > 0 ? selectedUser.assignedSites : ['N/A'],
            list: true,
          },
          { label: 'Status', value: selectedUser?.status || 'N/A' },
          { label: 'Created On', value: selectedUser?.createdOn || 'N/A' },
        ]}
      />

      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteRow(undefined);
        }}
        onDelete={handleDeleteRow}
        pageTitle="Admin User"
        // itemName={`User Name ${deleteRow?.blueNumberId}`}
        assignedProjects={deleteRow?.projects.map((proj) => proj.name)}
      />

      <ProjectUsersModal
        isOpen={isProjectUsersModalOpen}
        onClose={() => {
          setIsProjectUsersModalOpen(false);
          setSelectedUserForProject(null);
        }}
        blueNumberId={selectedUserForProject?.blueNumberId || ''}
        userName={selectedUserForProject?.blueNumberId}
        userId={selectedUserForProject?.id || 0}
      />
    </PageHeaderWrapper>
  );
};

export default SystemAdminUsers;
